// TopBar — the dashboard's top context strip. Org / project breadcrumb on
// the left, project-health pill on the right, and the appearance control
// (System / Light / Dark) at the far end.

import type { JSX } from "react";

import type { ProjectHealth } from "@loom/shared/dashboard";

import { useRelativeTime } from "../../i18n/format.js";
import { useT, type MessageKey } from "../../i18n/index.js";
import { ThemeToggle } from "../../theme/ThemeToggle.js";
import type { ProjectMeta } from "../useProjectMeta";

import { Icon } from "./Icon";

interface TopBarProps {
  readonly project: ProjectMeta | null;
  /**
   * The project's health, `null` while the summary that carries it is still
   * loading, and ABSENT on a host that does not fetch it at all.
   *
   * The three-way split is the fix: `null` used to mean both, so the issue page
   * — which never fetches `/api/projects/:id/dashboard` — showed "Checking…"
   * for as long as it was open, on every issue, with every request already
   * finished and green. A pill that is permanently checking is worse than no
   * pill: it says a check is running when none is.
   */
  readonly health?: ProjectHealth | null;
  readonly onSwitchProject: () => void;
}

const HEALTH_KEY: Record<ProjectHealth, MessageKey> = {
  green: "dashboard.topbar.health.green",
  amber: "dashboard.topbar.health.amber",
  red: "dashboard.topbar.health.red",
};

export const TopBar = ({
  project,
  health,
  onSwitchProject,
}: TopBarProps): JSX.Element => {
  const t = useT();
  // Localized. `lastTouched` is rendered server-side in English ("4m ago"), so
  // a German session read "aktualisiert 4m ago".
  const rel = useRelativeTime();
  return (
  <div className="topbar-mini">
    <div className="topbar-breadcrumb">
      <button
        className="btn btn-ghost btn-sm"
        onClick={onSwitchProject}
        style={{ padding: "4px 8px" }}
      >
        {project?.org && (
          <>
            <span className="org">{project.org}</span>
            <span className="sep">/</span>
          </>
        )}
        <span className="proj">
          {project?.name ?? "…"}
          <Icon name="chevron-down" size={11} />
        </span>
      </button>
    </div>
    <div className="topbar-health">
      {health ? (
        <>
          <span className={`health-dot ${health}`} />
          <span className="topbar-health-word">{t(HEALTH_KEY[health])}</span>
          <span className="sep">·</span>
        </>
      ) : health === null ? (
        <>
          <span className="skeleton is-dot" />
          <span className="topbar-health-word is-checking">{t("dashboard.topbar.checking")}</span>
          <span className="sep">·</span>
        </>
      ) : null}
      <span className="topbar-health-meta">
        {project?.lastTouchedIso === undefined
          ? t("dashboard.topbar.updated", { when: "—" })
          : t("dashboard.topbar.updated", { when: rel(project.lastTouchedIso) })}
      </span>
    </div>
    <ThemeToggle />
  </div>
  );
};
